import {
  type TimeTableData,
  type TimeTableDelay,
  weekDays,
} from "@/domain/contactdetails/timetables"
import { isBlank, isNotBlank, isNotEmptyArray, isNotNullNorUndefined } from "@/utils/ts-utils"
import { Box, Button, Grid, GridItem, Input, type PropsOf, Switch } from "@chakra-ui/react"
import { type CSSProperties, forwardRef, useImperativeHandle, useMemo, useState } from "react"
import { FormattedMessage } from "react-intl"
import { PlusIcon, TrashAltIcon } from "../Icons"
import { P2 } from "../Typographies"

type TimeTableDataEditDelay = {
  fh: string
  fm: string
  th: string
  tm: string
  appointmentOnly: boolean
}
type TimeTableDataEdit = {
  [K in keyof TimeTableData]-?: TimeTableDataEditDelay[]
}

const style: CSSProperties = {
  paddingInlineEnd: "0.5rem",
  paddingInlineStart: "0.5rem",
  width: "40px",
  textAlign: "center",
}
const props: Partial<PropsOf<typeof Input>> = {
  min: 0,
  // max: 12 or 59,
  step: 1,
  style,
  type: "number",
  variant: "outline",
}
const propsH: Partial<PropsOf<typeof Input>> = {
  ...props,
  max: 24,
  style: {
    ...style,
    marginLeft: "0.5rem",
  },
}
const propsM: Partial<PropsOf<typeof Input>> = {
  ...props,
  max: 59,
  style: {
    ...style,
    marginRight: "0.5rem",
  },
}
const propsS: Partial<PropsOf<typeof Switch>> = {
  ...props,
  style: {
    ...style,
    marginLeft: "0.5rem",
  },
}

const getTimeTableDelay = <
  T extends boolean,
  R = T extends true ? TimeTableDataEdit : TimeTableDelay,
>(
  edit: T,
  appointmentOnly?: boolean,
  fh?: string,
  fm?: string,
  th?: string,
  tm?: string
): R => {
  return edit
    ? ({
        fh: fh ?? "",
        fm: fm ?? "",
        th: th ?? "",
        tm: tm ?? "",
        appointmentOnly: appointmentOnly ?? false,
      } as R)
    : ({
        from: isNotBlank(`${fh}${fm}`) ? `${fh}:${fm?.padStart(2, "0")}` : "",
        to: isNotBlank(`${th}${tm}`) ? `${th}:${tm?.padStart(2, "0")}` : "",
        appointmentOnly: appointmentOnly ?? false,
      } as R)
}

const parseTimeTableValues = (timetable: TimeTableData) => {
  const edit = {} as TimeTableDataEdit
  weekDays.map(d => {
    edit[d] = []
    if (isNotEmptyArray(timetable[d])) {
      timetable[d]?.map(timetabledelay => {
        const appointmentOnly = timetabledelay.appointmentOnly ?? false
        const [fh, fm] = timetabledelay.from.split(":")
        const [th, tm] = (timetabledelay.to ?? "").split(":")
        edit[d].push(getTimeTableDelay(true, appointmentOnly, fh, fm, th, tm))
      })
    } else edit[d].push(getTimeTableDelay(true))
  })
  return edit
}

const buildTimeTableValues = (values: TimeTableDataEdit) => {
  const timetable = {} as TimeTableData
  weekDays.map(d => {
    if (isNotEmptyArray(values[d])) {
      timetable[d] = []
      values[d]?.map(td => {
        timetable[d]?.push(getTimeTableDelay(false, td.appointmentOnly, td.fh, td.fm, td.th, td.tm))
      })
    }
  })
  return timetable
}

const cleanValues = (
  delay: TimeTableDataEditDelay,
  k?: keyof TimeTableDataEditDelay,
  v?: string | boolean
) => {
  if (isNotBlank(k)) {
    if (k === "appointmentOnly") delay.appointmentOnly = v === true
    else delay[k] = (v as string) ?? ""
  }
  if (isNotBlank(delay.fh)) {
    delay.fh = delay.fh.padStart(2, "0")
    if (isBlank(delay.fm)) delay.fm = "00"
  }
  if (isNotBlank(delay.fm)) {
    delay.fm = delay.fm.padStart(2, "0")
  }
  if (isNotBlank(delay.th)) {
    delay.th = delay.th.padStart(2, "0")
    if (isBlank(delay.tm)) delay.tm = "00"
  }
  if (isNotBlank(delay.tm)) {
    delay.tm = delay.tm.padStart(2, "0")
  }
  return delay
}

type TimeTableEditorArgs = {
  value?: TimeTableData
  onChange?: (value: TimeTableData) => void
  onBlur?: (value: TimeTableData) => void
}

type TimeTableEditorProps = {
  getValue: () => TimeTableData
}

export const TimeTableEditor = forwardRef<TimeTableEditorProps, TimeTableEditorArgs>(
  ({ value: initialValue, onChange, onBlur }: TimeTableEditorArgs, ref) => {
    const [value, setValue] = useState<TimeTableData>(initialValue ?? {})
    const values = useMemo(() => parseTimeTableValues(value), [value])

    useImperativeHandle(ref, () => ({
      getValue: () => value,
    }))

    const addDelayToDay = (d: keyof TimeTableDataEdit) => {
      const new_v = { ...values }
      new_v[d].push(getTimeTableDelay(true, false, "00", "00"))
      setValue(buildTimeTableValues(new_v))
    }
    const rmvDelayToDay = (d: keyof TimeTableDataEdit, idx: number) => {
      const new_v = { ...values }
      new_v[d].splice(idx, 1)
      setValue(buildTimeTableValues(new_v))
    }

    const handleBlur = (
      d: keyof TimeTableDataEdit,
      idx: number,
      k: keyof TimeTableDataEditDelay,
      v: string | boolean
    ) => {
      const new_v = { ...values }

      // set value
      const delay = new_v[d][idx]
      if (isNotNullNorUndefined(delay)) {
        if (k === "appointmentOnly") delay[k] = v === true
        else delay[k] = (v as string) ?? ""
        new_v[d][idx] = delay
      }

      const new_d = new_v[d] as TimeTableDataEditDelay[]
      if (isNotNullNorUndefined(new_d))
        new_v[d] = new_d
          .map(td => cleanValues(td))
          .sort((a, b) =>
            isNotBlank(a.th) && isNotBlank(b.th)
              ? `${a.fh}${a.th}${a.fm}${a.tm}`.localeCompare(`${b.fh}${b.th}${b.fm}${b.tm}`)
              : `${a.fh}${a.fm}${a.th}${a.tm}`.localeCompare(`${b.fh}${b.fm}${b.th}${b.tm}`)
          )
      const new_value = buildTimeTableValues(new_v)
      setValue(new_value)
      // value will be updated only at next render => use new_value
      onChange?.(new_value)
      onBlur?.(new_value)
    }

    const hasRmvBtn = (idx: number) => {
      return idx > 0
    }

    const hasAddBtn = (d: keyof TimeTableDataEdit, idx: number) => {
      const day = values[d]
      const item = day[idx] as TimeTableDataEditDelay
      return (
        (values[d]?.length ?? 0) < 4 &&
        idx === (values[d]?.length ?? 0) - 1 &&
        isNotBlank(`${item.fh}${item.fm}${item.th}${item.tm}`)
      )
    }

    return (
      <Box>
        {weekDays.map(d => (
          <Grid templateColumns="repeat(5, 1fr)" gap={2} key={d}>
            <GridItem rowSpan={values[d]?.length ?? 1}>
              <FormattedMessage id={`timetables.${d}`} />
              {" : "}
            </GridItem>
            <GridItem colSpan={4}>
              {values[d]?.map((item, idx) => (
                <Box key={`${item.fh}-${item.fm}-${item.th}-${item.tm}`} display="block" pb={1}>
                  <FormattedMessage id="from" />
                  <Input
                    {...propsH}
                    defaultValue={item.fh ?? ""}
                    onBlur={e => handleBlur(d, idx, "fh", e.target.value)}
                  />
                  {"h"}
                  <Input
                    {...propsM}
                    defaultValue={item.fm ?? ""}
                    onBlur={e => handleBlur(d, idx, "fm", e.target.value)}
                  />
                  <FormattedMessage id="to" />
                  <Input
                    {...propsH}
                    defaultValue={item.th ?? ""}
                    onBlur={e => handleBlur(d, idx, "th", e.target.value)}
                  />
                  {"h"}
                  <Input
                    {...propsM}
                    defaultValue={item.tm ?? ""}
                    onBlur={e => handleBlur(d, idx, "tm", e.target.value)}
                  />
                  <Switch
                    {...propsS}
                    isChecked={item.appointmentOnly}
                    onChange={e => handleBlur(d, idx, "appointmentOnly", e.target.checked)}
                    size="sm"
                  />
                  <P2 mr={3}>
                    <FormattedMessage id="timetables.byRDV" />
                  </P2>
                  {hasRmvBtn(idx) && (
                    <Button
                      size="sm"
                      px={1}
                      mr={2}
                      onClick={() => rmvDelayToDay(d, idx)}
                      variant="outline"
                    >
                      <TrashAltIcon boxSize={4} />
                    </Button>
                  )}
                  {hasAddBtn(d, idx) && (
                    <Button size="sm" px={1} onClick={() => addDelayToDay(d)} variant="outline">
                      <PlusIcon boxSize={4} />
                    </Button>
                  )}
                </Box>
              ))}
            </GridItem>
          </Grid>
        ))}
      </Box>
    )
  }
)
